import React, { useEffect } from "react";
import {
  Card,
  Descriptions,
  Tag,
  Button,
  Row,
  Col,
  Steps,
  Typography,
  Divider,
  Space,
  Timeline,
  Avatar,
  Spin,
  message,
} from "antd";
import {
  ArrowLeftOutlined,
  EditOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  UserOutlined,
  StarOutlined,
  CalendarOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  TeamOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  fetchDossierById,
  clearCurrentDossier,
} from "../../../store/slices/dossierSlice";
import { RootState, AppDispatch } from "../../../store";
import { formatDate, formatDateTime } from "../../../utils/dateUtils";
import WorkflowProgress from "../WorkflowProgress/WorkflowProgress";
import {
  DetailContainer,
  DetailCard,
  WorkflowCard,
} from "./DossierDetail.styles";
import {
  formatCurrency,
  formatHonoraires,
  formatNDF,
} from "../../../utils/formatUtils";
import { dossierApi } from "../../../api/dossiers";

const { Title, Text } = Typography;
const { Step } = Steps;

const DossierDetail: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const { currentDossier, loading } = useSelector(
    (state: RootState) => state.dossiers
  );
  const { user } = useSelector((state: RootState) => state.auth);

  const calculateTotal = (
    honoraires?: number | null,
    ndf?: number | null
  ): number => {
    const honVal = Number(honoraires) || 0;
    const ndfVal = Number(ndf) || 0;
    return honVal + ndfVal;
  };

  useEffect(() => {
    if (id) {
      dispatch(fetchDossierById(Number(id)));
    }

    return () => {
      dispatch(clearCurrentDossier());
    };
  }, [dispatch, id]);

  const getEtatColor = (etat: string) => {
  const colors = {
    en_attente_visite: "blue",
    en_cours_visite: "processing",
    en_attente_elaboration: "orange",
    en_cours_elaboration: "processing",
    en_attente_validation: "gold",
    en_cours_validation: "processing",
    en_attente_modification: "red",
    en_cours_modification: "processing",
    modifie_att_validation: "purple",  // NOUVEAU
    valide_att_honoraires: "purple",
    valide_a_envoyer: "green",
    envoye: "success",
  };
  return colors[etat as keyof typeof colors] || "default";
};

const getEtatText = (etat: string) => {
  const texts = {
    en_attente_visite: "En attente de visite",
    en_cours_visite: "En cours de visite",
    en_attente_elaboration: "En attente d'élaboration",
    en_cours_elaboration: "En cours d'élaboration",
    en_attente_validation: "En attente de validation",
    en_cours_validation: "En cours de validation",
    en_attente_modification: "En attente de modification",
    en_cours_modification: "En cours de modification",
    modifie_att_validation: "Modifié - Att. validation",  // NOUVEAU
    valide_att_honoraires: "Validé - Att. honoraires",
    valide_a_envoyer: "Validé - À envoyer",
    envoye: "Envoyé",
  };
  return texts[etat as keyof typeof texts] || etat;
};

  const getPriorityColor = (priority: number) => {
    const colors = { 1: "default", 2: "orange", 3: "red" };
    return colors[priority as keyof typeof colors] || "default";
  };

  const getPriorityText = (priority: number) => {
    const texts = { 1: "Normale", 2: "Élevée", 3: "Critique" };
    return texts[priority as keyof typeof texts] || "Normale";
  };

  const getStatusText = (status: string) => {
    const texts = {
      DISPO: "Disponible",
      DISPO_ANCIEN: "Disponible Ancien",
      ATT_CLIENT: "Attente Client",
      ATT_EN_LIGNE: "Attente En Ligne",
      ATT_VISITEUR: "Attente Visiteur",
      ATT_TOPO: "Attente Topo",
      PROB: "Problème",
      A_DEMANDER_AU_TOPO: "À Demander au Topo",
      A_DEMANDER_EN_LIGNE: "À Demander En Ligne",
    };
    return texts[status as keyof typeof texts] || status;
  };

  if (loading || !currentDossier) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  return (
    <DetailContainer>
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 24,
        }}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/dossiers")}
            style={{ marginRight: 16 }}
          >
            Retour
          </Button>
          <div>
            <Title level={2} style={{ margin: 0 }}>
              Dossier {currentDossier.numero_dossier}
            </Title>
            <Text type="secondary">
              Client: {currentDossier.client} •{" "}
              {currentDossier.groupe_client_nom}
            </Text>
          </div>
        </div>

        <Space size="middle">
          <Tag
            color={getEtatColor(currentDossier.etat_dossier)}
            style={{ fontSize: "14px", padding: "4px 12px" }}
          >
            {getEtatText(currentDossier.etat_dossier)}
          </Tag>
          {user?.is_gestionnaire && (
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => navigate(`/dossiers/${currentDossier.id}/edit`)}
            >
              Modifier
            </Button>
          )}
        </Space>
      </div>

{currentDossier.etat_dossier === 'modifie_att_validation' && (
  <div style={{ 
    margin: '16px 0', 
    padding: '16px', 
    background: '#fff2e8', 
    border: '1px solid #ffbb96',
    borderRadius: 8 
  }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div>
        <Text strong style={{ color: '#d4380d' }}>
          <EditOutlined style={{ marginRight: 8 }} />
          Modifications terminées - En attente de validation
        </Text>
        <div style={{ marginTop: 4, fontSize: '12px', color: '#8c8c8c' }}>
          Les élaborateurs ont terminé leurs modifications. 
          <br />
          <strong>Actions possibles :</strong>
          <br />
          • Reprendre la validation et accepter (mettre date de fin)
          <br />
          • Demander de nouvelles modifications
        </div>
      </div>
      
      {(user?.is_gestionnaire || 
        currentDossier.validateurs?.some(v => v.user === user?.id)) && (
        <Button
          type="primary"
          icon={<CheckCircleOutlined />}
          onClick={async () => {
            try {
              await dossierApi.resumeValidation(currentDossier.id);
              message.success('Validation reprise');
              dispatch(fetchDossierById(currentDossier.id));
            } catch (error: any) {
              message.error(error.response?.data?.error || 'Erreur');
            }
          }}
        >
          Reprendre la validation
        </Button>
      )}
    </div>
  </div>
)}

      <Row gutter={[24, 24]}>
        {/* Informations générales */}
        <Col xs={24} lg={12}>
          <DetailCard
            title={
              <div style={{ display: "flex", alignItems: "center" }}>
                <FileTextOutlined
                  style={{ marginRight: 8, color: "#1890ff" }}
                />
                Informations générales
              </div>
            }
          >
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Numéro de dossier">
                <Text strong>{currentDossier.numero_dossier}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Date de réception">
                {formatDate(currentDossier.Date_reception_demande)}
              </Descriptions.Item>
              <Descriptions.Item label="Date d'envoi création">
                {currentDossier.Date_envoi_creation
                  ? formatDate(currentDossier.Date_envoi_creation)
                  : "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Client">
                {currentDossier.client}
              </Descriptions.Item>
              <Descriptions.Item label="Groupe client">
                {currentDossier.groupe_client_nom}
              </Descriptions.Item>
              <Descriptions.Item label="Typologie">
                {currentDossier.typologie_libelle}
              </Descriptions.Item>
              <Descriptions.Item label="Conservation">
                {currentDossier.conservation}
              </Descriptions.Item>
              <Descriptions.Item label="Titre foncier">
                {currentDossier.titre_foncier || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Indice">
                {currentDossier.indice || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Priorité">
                <Tag color={getPriorityColor(currentDossier.priorite)}>
                  {getPriorityText(currentDossier.priorite)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Gestionnaire">
                {currentDossier.gestionnaire_nom}
              </Descriptions.Item>
              <Descriptions.Item label="Date livraison convenue">
                {currentDossier.date_livraison_convenue
                  ? formatDate(currentDossier.date_livraison_convenue)
                  : "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Date envoi rapport">
                {currentDossier.date_envoi_rapport
                  ? formatDate(currentDossier.date_envoi_rapport)
                  : "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Dernière modification">
                {formatDateTime(currentDossier.date_modification)}
              </Descriptions.Item>
            </Descriptions>
          </DetailCard>
        </Col>

        {/* Section Facturation */}
        <Col xs={24} lg={12}>
          {/* <DetailCard
            title={
              <div style={{ display: "flex", alignItems: "center" }}>
                <DollarOutlined style={{ marginRight: 8, color: "#52c41a" }} />
                Facturation
              </div>
            }
          >
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <tbody>
                <tr>
                  <td
                    style={{
                      padding: "8px 16px 8px 0",
                      color: "#595959",
                      fontWeight: 500,
                      width: "45%",
                    }}
                  >
                    Honoraires HT :
                  </td>
                  <td
                    style={{
                      padding: "8px 0",
                      textAlign: "right",
                      width: "55%",
                    }}
                  >
                    {currentDossier.honoraires_ht ? (
                      <Text strong style={{ color: "#52c41a" }}>
                        {formatHonoraires(Number(currentDossier.honoraires_ht))}
                      </Text>
                    ) : (
                      <Text type="secondary">Non renseigné</Text>
                    )}
                  </td>
                </tr>
                <tr>
                  <td
                    style={{
                      padding: "8px 16px 8px 0",
                      color: "#595959",
                      fontWeight: 500,
                    }}
                  >
                    NDF (Note de frais) :
                  </td>
                  <td
                    style={{
                      padding: "8px 0",
                      textAlign: "right",
                    }}
                  >
                    {currentDossier.ndf_mad ? (
                      <Text strong style={{ color: "#1890ff" }}>
                        {formatNDF(Number(currentDossier.ndf_mad))}
                      </Text>
                    ) : (
                      <Text type="secondary">Non renseigné</Text>
                    )}
                  </td>
                </tr>
                {(currentDossier.honoraires_ht || currentDossier.ndf_mad) && (
                  <tr>
                    <td colSpan={2} style={{ padding: "8px 0" }}>
                      <div style={{ height: "1px", background: "#f0f0f0" }} />
                    </td>
                  </tr>
                )}
                {(currentDossier.honoraires_ht || currentDossier.ndf_mad) && (
                  <tr>
                    <td
                      style={{
                        padding: "8px 16px 8px 0",
                        color: "#722ed1",
                        fontWeight: 600,
                        fontSize: "14px",
                      }}
                    >
                      Total :
                    </td>
                    <td
                      style={{
                        padding: "8px 0",
                        textAlign: "right",
                      }}
                    >
                      <Text
                        strong
                        style={{ color: "#722ed1", fontSize: "16px" }}
                      >
                        {formatCurrency(
                          calculateTotal(
                            currentDossier.honoraires_ht,
                            currentDossier.ndf_mad
                          )
                        )}
                      </Text>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </DetailCard> */}
          <DetailCard
            title={
              <div style={{ display: "flex", alignItems: "center" }}>
                <EnvironmentOutlined
                  style={{ marginRight: 8, color: "#52c41a" }}
                />
                Localisation
              </div>
            }
          >
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Région">
                {currentDossier.region_nom}
              </Descriptions.Item>
              <Descriptions.Item label="Province">
                {currentDossier.province_nom}
              </Descriptions.Item>
              <Descriptions.Item label="Commune">
                {currentDossier.commune_nom}
              </Descriptions.Item>
              <Descriptions.Item label="Département">
                {currentDossier.departement_nom}
              </Descriptions.Item>
            </Descriptions>

            <Divider orientation="left">Statuts des documents</Divider>
            <Row gutter={[16, 8]}>
              <Col span={12}>
                <Text type="secondary">CP:</Text>
                <div>
                  {currentDossier.cp ? (
                    <Tag color="blue">{getStatusText(currentDossier.cp)}</Tag>
                  ) : (
                    <Text type="secondary">-</Text>
                  )}
                </div>
              </Col>
              <Col span={12}>
                <Text type="secondary">PC:</Text>
                <div>
                  {currentDossier.pc ? (
                    <Tag color="blue">{getStatusText(currentDossier.pc)}</Tag>
                  ) : (
                    <Text type="secondary">-</Text>
                  )}
                </div>
              </Col>
              <Col span={12}>
                <Text type="secondary">PA:</Text>
                <div>
                  {currentDossier.pa ? (
                    <Tag color="blue">{getStatusText(currentDossier.pa)}</Tag>
                  ) : (
                    <Text type="secondary">-</Text>
                  )}
                </div>
              </Col>
              <Col span={12}>
                <Text type="secondary">NR C CH:</Text>
                <div>
                  {currentDossier.nr_c_ch ? (
                    <Tag color="blue">
                      {getStatusText(currentDossier.nr_c_ch)}
                    </Tag>
                  ) : (
                    <Text type="secondary">-</Text>
                  )}
                </div>
              </Col>
            </Row>
          </DetailCard>
        </Col>

        {/* Localisation */}
        {/* <Col xs={24} lg={12}>
          <DetailCard 
            title={
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <EnvironmentOutlined style={{ marginRight: 8, color: '#52c41a' }} />
                Localisation
              </div>
            }
          >
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Région">
                {currentDossier.region_nom}
              </Descriptions.Item>
              <Descriptions.Item label="Province">
                {currentDossier.province_nom}
              </Descriptions.Item>
              <Descriptions.Item label="Commune">
                {currentDossier.commune_nom}
              </Descriptions.Item>
              <Descriptions.Item label="Département">
                {currentDossier.departement_nom}
              </Descriptions.Item>
            </Descriptions>

            <Divider orientation="left">Statuts des documents</Divider>
            <Row gutter={[16, 8]}>
              <Col span={12}>
                <Text type="secondary">CP:</Text>
                <div>
                  {currentDossier.cp ? (
                    <Tag color="blue" >
                      {getStatusText(currentDossier.cp)}
                    </Tag>
                  ) : (
                    <Text type="secondary">-</Text>
                  )}
                </div>
              </Col>
              <Col span={12}>
                <Text type="secondary">PC:</Text>
                <div>
                  {currentDossier.pc ? (
                    <Tag color="blue" >
                      {getStatusText(currentDossier.pc)}
                    </Tag>
                  ) : (
                    <Text type="secondary">-</Text>
                  )}
                </div>
              </Col>
              <Col span={12}>
                <Text type="secondary">PA:</Text>
                <div>
                  {currentDossier.pa ? (
                    <Tag color="blue" >
                      {getStatusText(currentDossier.pa)}
                    </Tag>
                  ) : (
                    <Text type="secondary">-</Text>
                  )}
                </div>
              </Col>
              <Col span={12}>
                <Text type="secondary">NR C CH:</Text>
                <div>
                  {currentDossier.nr_c_ch ? (
                    <Tag color="blue" >
                      {getStatusText(currentDossier.nr_c_ch)}
                    </Tag>
                  ) : (
                    <Text type="secondary">-</Text>
                  )}
                </div>
              </Col>
            </Row>
          </DetailCard>
        </Col> */}

        {/* Progression du workflow */}
        <Col span={24}>
          <WorkflowCard
            title={
              <div style={{ display: "flex", alignItems: "center" }}>
                <TeamOutlined style={{ marginRight: 8, color: "#722ed1" }} />
                Progression du workflow
              </div>
            }
          >
            {currentDossier.visiteurs ||
            currentDossier.elaborateurs ||
            currentDossier.validateurs ? (
              <WorkflowProgress
                visiteurs={currentDossier.visiteurs || []}
                elaborateurs={currentDossier.elaborateurs || []}
                validateurs={currentDossier.validateurs || []}
              />
            ) : (
              <div style={{ textAlign: "center", padding: "40px 0" }}>
                <Text type="secondary">
                  Aucune assignation de workflow pour le moment
                </Text>
              </div>
            )}
          </WorkflowCard>
        </Col>

        {/* Détails des assignations */}
        {(currentDossier.visiteurs?.length ||
          currentDossier.elaborateurs?.length ||
          currentDossier.validateurs?.length) && (
          <Col span={24}>
            <DetailCard title="Détails des assignations">
              <Row gutter={[24, 24]}>
                {/* Visiteurs */}
                {currentDossier.visiteurs &&
                  currentDossier.visiteurs.length > 0 && (
                    <Col xs={24} md={8}>
                      <div style={{ marginBottom: 16 }}>
                        <Title
                          level={5}
                          style={{ color: "#1890ff", margin: 0 }}
                        >
                          <EyeOutlined style={{ marginRight: 8 }} />
                          Visiteurs ({currentDossier.visiteurs.length})
                        </Title>
                      </div>
                      <Timeline>
                        {currentDossier.visiteurs.map((visiteur, index) => (
                          <Timeline.Item
                            key={index}
                            dot={
                              visiteur.date_completed ? (
                                <CheckCircleOutlined
                                  style={{ color: "#52c41a" }}
                                />
                              ) : visiteur.date_started ? (
                                <ClockCircleOutlined
                                  style={{ color: "#faad14" }}
                                />
                              ) : (
                                <UserOutlined style={{ color: "#d9d9d9" }} />
                              )
                            }
                          >
                            <div>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  flexWrap: "wrap",
                                  gap: 4,
                                }}
                              >
                                <Text strong>{visiteur.user_name}</Text>
                                {visiteur.is_primary && (
                                  <StarOutlined style={{ color: "#faad14" }} />
                                )}
                                {visiteur.user_is_active === false && (
                                  <Tag
                                    color="orange"
                                    style={{
                                      fontSize: "10px",
                                      lineHeight: "16px",
                                    }}
                                  >
                                    Inactif
                                  </Tag>
                                )}
                              </div>
                              <div style={{ fontSize: "12px", color: "#666" }}>
                                {visiteur.user_email}
                              </div>
                              {visiteur.date_started && (
                                <div
                                  style={{ fontSize: "11px", color: "#999" }}
                                >
                                  Début: {formatDate(visiteur.date_started)}
                                </div>
                              )}
                              {visiteur.date_completed && (
                                <div
                                  style={{ fontSize: "11px", color: "#52c41a" }}
                                >
                                  Terminé: {formatDate(visiteur.date_completed)}
                                </div>
                              )}
                            </div>
                          </Timeline.Item>
                        ))}
                      </Timeline>
                    </Col>
                  )}

                {/* Élaborateurs */}
                {currentDossier.elaborateurs &&
                  currentDossier.elaborateurs.length > 0 && (
                    <Col xs={24} md={8}>
                      <div style={{ marginBottom: 16 }}>
                        <Title
                          level={5}
                          style={{ color: "#722ed1", margin: 0 }}
                        >
                          <EditOutlined style={{ marginRight: 8 }} />
                          Élaborateurs ({currentDossier.elaborateurs.length})
                        </Title>
                      </div>
                      <Timeline>
                        {currentDossier.elaborateurs.map(
                          (elaborateur, index) => (
                            <Timeline.Item
                              key={index}
                              dot={
                                elaborateur.date_completed ? (
                                  <CheckCircleOutlined
                                    style={{ color: "#52c41a" }}
                                  />
                                ) : elaborateur.date_started ? (
                                  <ClockCircleOutlined
                                    style={{ color: "#faad14" }}
                                  />
                                ) : (
                                  <UserOutlined style={{ color: "#d9d9d9" }} />
                                )
                              }
                            >
                              <div>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    flexWrap: "wrap",
                                    gap: 4,
                                  }}
                                >
                                  <Text strong>{elaborateur.user_name}</Text>
                                  {elaborateur.is_primary && (
                                    <StarOutlined
                                      style={{ color: "#faad14" }}
                                    />
                                  )}
                                  {elaborateur.user_is_active === false && (
                                    <Tag
                                      color="orange"
                                      style={{
                                        fontSize: "10px",
                                        lineHeight: "16px",
                                      }}
                                    >
                                      Inactif
                                    </Tag>
                                  )}
                                </div>
                                <div
                                  style={{ fontSize: "12px", color: "#666" }}
                                >
                                  {elaborateur.user_email}
                                </div>
                                {elaborateur.date_started && (
                                  <div
                                    style={{ fontSize: "11px", color: "#999" }}
                                  >
                                    Début:{" "}
                                    {formatDate(elaborateur.date_started)}
                                  </div>
                                )}
                                {elaborateur.date_completed && (
                                  <div
                                    style={{
                                      fontSize: "11px",
                                      color: "#52c41a",
                                    }}
                                  >
                                    Terminé:{" "}
                                    {formatDate(elaborateur.date_completed)}
                                  </div>
                                )}

                                {/* NOUVELLE SECTION: Historique des modifications */}
                                {elaborateur.modification_history &&
                                  elaborateur.modification_history.length >
                                    0 && (
                                    <div
                                      style={{
                                        marginTop: 8,
                                        padding: 8,
                                        background: "#fff7e6",
                                        borderRadius: 4,
                                        border: "1px solid #ffd591",
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontSize: "11px",
                                          fontWeight: 600,
                                          color: "#d46b08",
                                          marginBottom: 6,
                                          display: "flex",
                                          alignItems: "center",
                                        }}
                                      >
                                        <EditOutlined
                                          style={{
                                            marginRight: 4,
                                            fontSize: "10px",
                                          }}
                                        />
                                        Modifications (
                                        {
                                          elaborateur.modification_history
                                            .length
                                        }
                                        )
                                      </div>

                                      {elaborateur.modification_history.map(
                                        (mod, modIndex) => (
                                          <div
                                            key={mod.id}
                                            style={{
                                              marginBottom:
                                                modIndex <
                                                elaborateur.modification_history!
                                                  .length -
                                                  1
                                                  ? 6
                                                  : 0,
                                              paddingBottom:
                                                modIndex <
                                                elaborateur.modification_history!
                                                  .length -
                                                  1
                                                  ? 6
                                                  : 0,
                                              borderBottom:
                                                modIndex <
                                                elaborateur.modification_history!
                                                  .length -
                                                  1
                                                  ? "1px solid #ffe7ba"
                                                  : "none",
                                            }}
                                          >
                                            <div
                                              style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 4,
                                                marginBottom: 2,
                                              }}
                                            >
                                              {mod.is_completed ? (
                                                <CheckCircleOutlined
                                                  style={{
                                                    color: "#52c41a",
                                                    fontSize: "10px",
                                                  }}
                                                />
                                              ) : (
                                                <ClockCircleOutlined
                                                  style={{
                                                    color: "#faad14",
                                                    fontSize: "10px",
                                                  }}
                                                />
                                              )}
                                              <Text
                                                style={{
                                                  fontSize: "10px",
                                                  fontWeight: 500,
                                                  color: mod.is_completed
                                                    ? "#52c41a"
                                                    : "#faad14",
                                                }}
                                              >
                                                {mod.is_completed
                                                  ? "Terminée"
                                                  : "En cours"}
                                              </Text>
                                            </div>

                                            <div
                                              style={{
                                                fontSize: "9px",
                                                color: "#8c8c8c",
                                                marginBottom: 2,
                                              }}
                                            >
                                              Demandée:{" "}
                                              {formatDateTime(
                                                mod.date_requested
                                              )}
                                            </div>

                                            {mod.date_completed && (
                                              <div
                                                style={{
                                                  fontSize: "9px",
                                                  color: "#52c41a",
                                                  marginBottom: 2,
                                                }}
                                              >
                                                Terminée:{" "}
                                                {formatDate(mod.date_completed)}
                                              </div>
                                            )}

                                            {mod.observations && (
                                              <div
                                                style={{
                                                  fontSize: "9px",
                                                  color: "#595959",
                                                  fontStyle: "italic",
                                                  padding: 4,
                                                  background: "#fafafa",
                                                  borderRadius: 2,
                                                  marginTop: 2,
                                                  border: "1px solid #f0f0f0",
                                                }}
                                              >
                                                "{mod.observations}"
                                              </div>
                                            )}
                                          </div>
                                        )
                                      )}
                                    </div>
                                  )}
                              </div>
                            </Timeline.Item>
                          )
                        )}
                      </Timeline>
                    </Col>
                  )}

                {/* Validateurs */}
                {currentDossier.validateurs &&
                  currentDossier.validateurs.length > 0 && (
                    <Col xs={24} md={8}>
                      <div style={{ marginBottom: 16 }}>
                        <Title
                          level={5}
                          style={{ color: "#52c41a", margin: 0 }}
                        >
                          <CheckCircleOutlined style={{ marginRight: 8 }} />
                          Validateurs ({currentDossier.validateurs.length})
                        </Title>
                      </div>
                      <Timeline>
                        {currentDossier.validateurs.map((validateur, index) => (
                          <Timeline.Item
                            key={index}
                            dot={
                              validateur.date_completed ? (
                                <CheckCircleOutlined
                                  style={{ color: "#52c41a" }}
                                />
                              ) : validateur.date_started ? (
                                <ClockCircleOutlined
                                  style={{ color: "#faad14" }}
                                />
                              ) : (
                                <UserOutlined style={{ color: "#d9d9d9" }} />
                              )
                            }
                          >
                            <div>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  flexWrap: "wrap",
                                  gap: 4,
                                }}
                              >
                                <Text strong>{validateur.user_name}</Text>
                                {validateur.is_primary && (
                                  <StarOutlined style={{ color: "#faad14" }} />
                                )}
                                {validateur.user_is_active === false && (
                                  <Tag
                                    color="orange"
                                    style={{
                                      fontSize: "10px",
                                      lineHeight: "16px",
                                    }}
                                  >
                                    Inactif
                                  </Tag>
                                )}
                              </div>
                              <div style={{ fontSize: "12px", color: "#666" }}>
                                {validateur.user_email}
                              </div>
                              {validateur.date_prevalidation && (
                                <div
                                  style={{ fontSize: "11px", color: "#faad14" }}
                                >
                                  Prévalidation:{" "}
                                  {formatDate(validateur.date_prevalidation)}
                                </div>
                              )}
                              {validateur.date_started && (
                                <div
                                  style={{ fontSize: "11px", color: "#999" }}
                                >
                                  Début: {formatDate(validateur.date_started)}
                                </div>
                              )}
                              {validateur.date_completed && (
                                <div
                                  style={{ fontSize: "11px", color: "#52c41a" }}
                                >
                                  Terminé:{" "}
                                  {formatDate(validateur.date_completed)}
                                </div>
                              )}
                            </div>
                          </Timeline.Item>
                        ))}
                      </Timeline>
                    </Col>
                  )}
              </Row>
            </DetailCard>
          </Col>
        )}

       
      </Row>
    </DetailContainer>
  );
};

export default DossierDetail;
